<script setup lang="ts">
import type { TutorialVideo } from '~/utils/crystallize/tutorial-video'
import { faClockSeven } from '@fortawesome/pro-light-svg-icons'
import { faPlay } from '@fortawesome/pro-solid-svg-icons'
import { onWatcherCleanup } from 'vue'
import { getSrc } from '~/utils/crystallize/tutorial-video'

const props = defineProps<{
  video: TutorialVideo
}>()

const src = computed(() => getSrc(props.video))

const videoRef = useTemplateRef('video')

const duration = ref<string>()

watch(videoRef, (el) => {
  function updateDuration() {
    if (el?.duration) {
      const mins = Math.floor(el.duration / 60)
      const secs = Math.floor(el.duration % 60)
      duration.value = `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
    }
  }

  updateDuration()

  el?.addEventListener('loadedmetadata', updateDuration)
  el?.addEventListener('durationchange', updateDuration)

  onWatcherCleanup(() => {
    el?.removeEventListener('loadedmetadata', updateDuration)
    el?.removeEventListener('durationchange', updateDuration)
  })
}, {
  immediate: true,
})

const thumbnail = computed(() => {
  const video = props.video.components?.video
  if (typeof video !== 'string') {
    return video?.firstVideo?.thumbnails?.[0]
  }

  return null
})
</script>

<template>
  <component :is="$attrs.is ?? 'li'" v-if="video.components?.video" class="flex flex-col gap-5">
    <!-- eslint-disable-next-line vuejs-accessibility/media-has-caption -->
    <video v-if="src" ref="video" class="hidden" :src="src" preload="metadata" />
    <NuxtLink :to="video.path" class="rounded-2xl no-underline overflow-hidden aspect-video relative group hover:contrast-75">
      <CrystallizeImage
        v-if="thumbnail"
        :image="thumbnail"
        img-class="group-hover:scale-105 object-cover w-full transition-transform"
        sizes="(max-width: 639px) 90vw, (max-width: 1023px) 50vw, 550px"
      />
      <div v-else class="overflow-hidden bg-blurple-400 size-full flex justify-center">
        <IconsLogoJuridika class="group-hover:scale-105 transition-transform fill-white w-3/5 h-full" />
      </div>

      <div class="flex items-center justify-between bg-blurple-500/80 text-white px-8 py-5 absolute bottom-0 w-full gap-x-3">
        <h2 v-if="video.components.title" class="title-l" v-text="video.components.title" />
        <Shape variant="octagon" class="size-14 fill-moss-200 min-w-14 text-blurple-500/80" :icon="faPlay" />
      </div>
    </NuxtLink>

    <div class="flex flex-col gap-3">
      <div class="flex gap-4 items-center">
        <div class="flex items-center gap-3">
          <Icon :icon="faClockSeven" class="text-blurple-300" />
          <span v-text="duration ?? '00:00'" />
        </div>

        <TutorialVideoChapters :video="video" />
      </div>

      <div class="line-clamp-4 md:pr-l">
        <CrystallizeRichText
          v-for="(block, i) in video.components.description"
          :key="i"
          :block="block"
        />
      </div>

      <CrystallizePublished icon :date="video.components.published" class="[&_svg]:text-blurple-300 [&_span]:text-small" />
    </div>
  </component>
</template>
